import { Injectable } from '@angular/core';

import { filter, map, tap, combineLatest, Observable, merge } from 'rxjs';

import { makeMap } from '@shared/core';
import { User } from '@shared/user/models';
import { Location } from '@shared/location/models';
import { UserWithAccount } from '@tablesready/common-ui';
import {
  UsersService,
  AccountUsersService,
  LocationUsersService,
} from '../../../../../services';
import { LocationsService } from '@tablesready/frontend-store';

@Injectable()
export class UsersListService {
  private usersMap: Record<string, User>;
  private locationsMap: Record<string, Location>;

  constructor(
    private readonly usersService: UsersService,
    private readonly accountUsersService: AccountUsersService,
    private readonly locationUsersService: LocationUsersService,
    private readonly locationsService: LocationsService,
  ) {}

  load() {
    return combineLatest([
      this.usersService.load(),
      this.accountUsersService.load(),
      this.locationUsersService.load(),
      this.locationsService.load(),
    ]);
  }

  getUsersAndLocations(): Observable<[UserWithAccount[], Location[]]> {
    return combineLatest([
      this.usersService.selectAll().pipe(tap(users => this.usersMap = makeMap(users))),
      this.accountUsersService.selectAll(),
      this.locationUsersService.selectAll(),
      this.locationsService.selectAll().pipe(
        filter(locations => locations.length > 0),
        tap(locations => this.locationsMap = makeMap(locations)),
      ),
    ]).pipe(
      map(([, accountUsers, locationUsers, locations]) => {
        const usersData = [];

        for (let accountUser of accountUsers) {
          const accountLocationUsers = [];
          const user = this.usersMap[accountUser.userId];

          for (let locationUser of locationUsers) {
            if (locationUser.userId === user._id) {
              const loc = this.locationsMap[locationUser.locationId];

              if (loc !== undefined) {
                const { name } = loc;
                accountLocationUsers.push({ ...locationUser, locationName: name });
              }
            }
          }

          usersData.push({
            ...accountUser,
            name: user.name,
            email: user.email,
            locationUsers: accountLocationUsers,
          });
        }

        return [usersData, locations];
      }));
  }

  listenToChanges() {
    return merge(
      this.usersService.listenToChanges(),
      this.accountUsersService.listenToChanges(),
      this.locationUsersService.listenToChanges(),
      this.locationsService.listenToChanges(),
    );
  }

  getLocationMap() {
    return this.locationsMap;
  }
}
